/**
 * Shared scorecard *template editor* built on the kula-ai
 * `ScorecardTemplateBuilder`. Drop-in replacement for the legacy Formik
 * `ScoreCard` (which wrapped `@/components/score-card-builder`), adopted by
 * one host at a time.
 *
 * Behaviour parity with the legacy editor is deliberate:
 *   - template auto-select for brand-new activities (`activityAction === 'new'`)
 *   - the brief `changeTemplateInProgress` spinner that remounts the builder so
 *     a freshly picked template re-seeds its questions (the kula-ai builder, like
 *     the old Formik one, only reads `initialValues` on mount)
 *   - the optional "Include scorecards" toggle + Fill-with-AI block
 *
 * Hosts drive it through the imperative handle: `validate()` before save,
 * `getValues()` to read the form back. Mapping the returned
 * `ScorecardTemplateFormValues` into each host's payload stays in the host
 * (e.g. via `getScorecardPayload`), so the wire contract is unchanged.
 */
import { Flex, Box, Center, Spinner, Switch, Image } from '@chakra-ui/react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import qs from 'qs';
import {
  ScorecardTemplateBuilder,
  type ScorecardTemplateBuilderHandle,
  type ScorecardTemplateFormValues,
} from '@kula-ai/components';
import { ScorecardAddQuestion } from '@/components/shared/scorecard/ScorecardAddQuestion';
import scorecardIcon from '@/assets/svg/scorecard/scorecard-icon.svg';
import { KText } from '@/ui-library/components';
import { getScorecardInitialValues } from '@/utils/schedule-interview-helpers';
import { API_SERVICE } from '@/api/interview-plan';
import { FormBuilder } from '@/components/formBuilder';
import { customTemplateFormatLabel } from '@/components/shared/customTemplateLabel';
import {
  isScorecardEdited,
  templateOptionToFormValues,
} from '@/components/shared/scorecardTemplateDiff';
import { useInterviewStageManager } from '@/context-providers/interviewStageManager';
import { ScoreCardFillWithAi } from '../ScoreCardFillWithAi';
import { useScorecardVisibilitySettings } from '@/api/scorecard-visibility';

interface ScoreCardData {
  feedback_description?: string | null;
  scorecard_feedback_description?: string | null;
  is_feedback_mandatory?: boolean | null;
  rating_description?: string | null;
  scorecard_rating_description?: string | null;
}

export interface ScorecardTemplateEditorHandle {
  /** Validate the builder. Resolves true when the scorecard is not required. */
  validate: () => Promise<boolean>;
  /** Current builder values (scoreCards + descriptions + feedback flag). */
  getValues: () => ScorecardTemplateFormValues;
  // Formik-compatible aliases — let legacy hosts adopt the editor by swapping
  // the component + ref only, keeping their existing save handlers. They read
  // `.submitForm()` / `.isValid` / `.values` exactly as on the old Formik ref.
  /** Alias of {@link validate}; also records the result for `.isValid`. */
  submitForm: () => Promise<boolean>;
  /** Alias of {@link validate} (Formik `.validateForm()` shim). */
  validateForm: () => Promise<boolean>;
  /** Result of the last validation run (Formik `.isValid` shim). */
  readonly isValid: boolean;
  /** Current builder values (Formik `.values` shim). */
  readonly values: ScorecardTemplateFormValues;
  /** Errors from the last validation run (Formik `.errors` shim). */
  readonly errors: Record<string, unknown>;
  /** Set/clear validation errors (Formik `.setErrors()` shim — empty ⇒ valid). */
  setErrors: (_errors?: Record<string, unknown>) => void;
}

export interface ScorecardTemplateEditorProps {
  currentTab?: string;
  location?: string | null;
  fill_with_ai_enabled?: boolean;
  note_taker_enabled?: boolean;
  onFillWithAiChange?: ((_checked: boolean) => void) | null;
  scorecardData?: ScoreCardData;
  onChange?: (_values: ScorecardTemplateFormValues) => void;
  questions?: unknown[];
  note?: string;
  wrapperProps?: Record<string, unknown>;
  defaultScorecardParams?: Record<string, unknown>;
  onTemplateSelect?: (_template: unknown) => void;
  scorecardRequired?: boolean;
  onScorecardRequiredChange?: ((_required: boolean) => void) | null;
  toggleLabel?: string;
}

const EMPTY_VALUES: ScorecardTemplateFormValues = {
  scoreCards: [],
  feedback_description: '',
  is_feedback_mandatory: false,
  rating_description: '',
};

export const ScorecardTemplateEditor = forwardRef<
  ScorecardTemplateEditorHandle,
  ScorecardTemplateEditorProps
>(
  (
    {
      currentTab = '',
      location = null,
      fill_with_ai_enabled = false,
      note_taker_enabled = false,
      onFillWithAiChange = null,
      scorecardData = {},
      onChange,
      questions = [],
      note = 'This is the recommended scorecard template. You can customize it but changes will only apply to this interview.',
      wrapperProps = {},
      defaultScorecardParams = {},
      onTemplateSelect,
      scorecardRequired = true,
      onScorecardRequiredChange = null,
      toggleLabel = 'Include scorecards in this assessment',
    },
    ref
  ) => {
    const builderRef = useRef<ScorecardTemplateBuilderHandle>(null);
    // Mirrors Formik's `.isValid`: updated by every validate()/submitForm() so
    // hosts that read `.isValid` right after `submitForm()` see the result.
    const lastValidRef = useRef(true);
    // Mirrors Formik's `.errors` for the same hosts (Settings reads
    // `Object.keys(errors).length` and clears via `setErrors({})`).
    const lastErrorsRef = useRef<Record<string, unknown>>({});
    // The template-select FormBuilder keeps its own Formik instance — it never
    // shares state with the kula-ai builder (it only drives `onChange`).
    const templateFormRef = useRef(null);
    const { activityAction } = useInterviewStageManager();
    const { data: scorecardVisibilityData } = useScorecardVisibilitySettings();
    const showFillWithAi =
      scorecardVisibilityData?.data?.account_setting?.fill_with_ai_enabled;

    const [initialValues, setInitialValues] = useState<
      Partial<ScorecardTemplateFormValues>
    >(() => ({
      scoreCards: getScorecardInitialValues(questions),
      feedback_description:
        scorecardData?.feedback_description ||
        scorecardData?.scorecard_feedback_description ||
        '',
      is_feedback_mandatory: scorecardData?.is_feedback_mandatory ?? true,
      rating_description:
        scorecardData?.rating_description ||
        scorecardData?.scorecard_rating_description ||
        '',
    }));

    const [changeTemplateInProgress, setChangeTemplateInProgress] =
      useState(false);

    const templateParams = useMemo(
      () =>
        qs.stringify(defaultScorecardParams, {
          arrayFormat: 'brackets',
        }),
      [defaultScorecardParams]
    );

    const { data: scorecardTemplates = [], isLoading } = useQuery({
      queryKey: [`ats_scorecards/list_templates/${templateParams}`],
      queryFn: () => API_SERVICE.getScoreCardTemplates(templateParams),
      staleTime: 1 * 60 * 1000,
      enabled: Object.keys(defaultScorecardParams).length > 0,
    });

    const templateOptions = useMemo(
      () =>
        (scorecardTemplates ?? []).map((template) => ({
          value: template?.name,
          label: template?.name,
          questions: template?.questions,
          feedback_description: template?.feedback_description,
          rating_description: template?.rating_description,
          is_feedback_mandatory:
            template?.scorecard_config?.is_feedback_mandatory ??
            template?.is_feedback_mandatory ??
            false,
        })),
      [scorecardTemplates]
    );

    // Drives the "Custom" label once the questions leave the template.
    const [pickedTemplate, setPickedTemplate] = useState(null);
    const [templateEdited, setTemplateEdited] = useState(false);

    const handleBuilderChange = useCallback(
      (values) => {
        onChange?.(values);
        setTemplateEdited(
          isScorecardEdited(values, templateOptionToFormValues(pickedTemplate))
        );
      },
      [onChange, pickedTemplate]
    );

    const applyTemplate = useCallback((selectedTemplate) => {
      setPickedTemplate(selectedTemplate);
      setTemplateEdited(false);
      // The picker owns a separate Formik instance; seeding only the builder
      // left it showing its placeholder for an auto-selected template.
      templateFormRef.current?.setFieldValue(
        'scorecard_template',
        selectedTemplate?.value ?? null
      );
      setChangeTemplateInProgress(true);
      setInitialValues({
        scoreCards: getScorecardInitialValues(
          selectedTemplate?.questions || []
        ),
        feedback_description: selectedTemplate?.feedback_description || '',
        is_feedback_mandatory: selectedTemplate?.is_feedback_mandatory ?? false,
        rating_description: selectedTemplate?.rating_description || '',
      });
      // Brief unmount/remount window so the builder re-seeds from the new
      // template (it reads `initialValues` only on mount).
      setTimeout(() => setChangeTemplateInProgress(false), 100);
    }, []);

    const templatesFormConfig = [
      {
        name: 'scorecard_template',
        label: 'Scorecard template',
        type: 'select',
        isAppendToBody: true,
        placeholder: 'Select scorecard template',
        options: templateOptions,
        customFormatLabel: customTemplateFormatLabel(templateEdited),
        customOnChange: (selectedTemplate) => {
          onTemplateSelect?.(selectedTemplate);
          applyTemplate(selectedTemplate);
        },
      },
    ];

    useEffect(() => {
      if (
        scorecardTemplates?.length > 0 &&
        questions?.length === 0 &&
        activityAction?.type === 'new'
      ) {
        const [first] = scorecardTemplates;
        const defaultItem = {
          value: first.name,
          label: first.name,
          questions: first.questions,
          feedback_description: first.feedback_description,
          is_feedback_mandatory:
            first.scorecard_config?.is_feedback_mandatory ??
            first.is_feedback_mandatory ??
            true,
          rating_description: first.rating_description,
        };
        const id = setTimeout(() => applyTemplate(defaultItem), 0);
        return () => clearTimeout(id);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scorecardTemplates]);

    useImperativeHandle(ref, () => {
      const runValidation = async () => {
        if (!scorecardRequired) {
          lastValidRef.current = true;
          lastErrorsRef.current = {};
          return true;
        }
        const ok = (await builderRef.current?.trigger()) ?? true;
        lastValidRef.current = ok;
        lastErrorsRef.current = (builderRef.current?.formState?.errors ??
          {}) as Record<string, unknown>;
        return ok;
      };
      const readValues = () => builderRef.current?.getValues() ?? EMPTY_VALUES;
      return {
        validate: runValidation,
        getValues: readValues,
        submitForm: runValidation,
        validateForm: runValidation,
        get isValid() {
          return lastValidRef.current;
        },
        get values() {
          return readValues();
        },
        get errors() {
          return lastErrorsRef.current;
        },
        setErrors: (next?: Record<string, unknown>) => {
          const e = next ?? {};
          lastErrorsRef.current = e;
          // Mirror Formik: an empty error map means the form is valid.
          lastValidRef.current = Object.keys(e).length === 0;
        },
      };
    }, [scorecardRequired]);

    return (
      <Flex p="16px 24px" flexDir="column" {...wrapperProps}>
        {onScorecardRequiredChange && (
          <Flex
            alignItems="center"
            justifyContent="space-between"
            p="16px"
            bg="secondary-25"
            borderRadius="6px"
            mb={scorecardRequired ? '12px' : '0'}
          >
            <Flex alignItems="center" gap="12px">
              <Image
                src={scorecardIcon}
                alt=""
                width="35px"
                height="45px"
                flexShrink={0}
              />
              <KText size="sm-semi-bold" color="secondary-900">
                {toggleLabel}
              </KText>
            </Flex>
            <Switch
              className={`ats-switch-toogle ${scorecardRequired ? 'active' : ''}`}
              colorScheme="ats-primary"
              isChecked={scorecardRequired}
              onChange={(e) => onScorecardRequiredChange(e.target.checked)}
            />
          </Flex>
        )}
        <Flex
          flexDir="column"
          gap="12px"
          h={scorecardRequired ? 'auto' : '0'}
          overflow={scorecardRequired ? 'visible' : 'hidden'}
        >
          {isLoading && (
            <Center h="50vh" flex="1">
              <Spinner />
            </Center>
          )}
          {!isLoading && (
            <>
              {showFillWithAi && onFillWithAiChange && (
                <ScoreCardFillWithAi
                  currentTab={currentTab}
                  showFillWithAi={showFillWithAi}
                  location={location}
                  fill_with_ai_enabled={fill_with_ai_enabled}
                  note_taker_enabled={note_taker_enabled}
                  onFillWithAiChange={onFillWithAiChange}
                />
              )}
              {scorecardTemplates?.length > 0 && (
                <Box pos="relative">
                  <FormBuilder
                    formRef={templateFormRef}
                    config={templatesFormConfig}
                  />
                </Box>
              )}
              <Flex
                alignItems="center"
                p="15px"
                background={note ? 'ats-bg-primary' : 'transparent'}
                borderRadius="6px"
                gap="12px"
                justifyContent="center"
              >
                {note && (
                  <KText as="span">
                    <b>Note:</b> {note}
                  </KText>
                )}
                <ScorecardAddQuestion formRef={builderRef} />
              </Flex>
              <Box>
                {changeTemplateInProgress && (
                  <Center h="50vh" flex="1">
                    <Spinner />
                  </Center>
                )}
                {!changeTemplateInProgress && (
                  <ScorecardTemplateBuilder
                    key={JSON.stringify(initialValues)}
                    formRef={builderRef}
                    initialValues={initialValues}
                    onChange={handleBuilderChange}
                    // 0 questions stays valid — matches the legacy builder,
                    // which never enforced a minimum.
                    minQuestions={0}
                  />
                )}
              </Box>
            </>
          )}
        </Flex>
      </Flex>
    );
  }
);

ScorecardTemplateEditor.displayName = 'ScorecardTemplateEditor';
